import { Canceler } from 'axios';
import { action, makeObservable, observable } from 'mobx';
import { OrdersHistoryStore } from '../ordersHistoryStore/ordersHistoryStore';
import { CallCenterStore } from '../ordersStore/callCenterStore';
import { LiveOrdersStore } from '../ordersStore/liveOrdersStore';
import { localeStore } from '../localesStore';
import { DELIVERY_TYPES, getOrderDeliveryType } from 'src/constants/deliveryTypes';
import { ORDER_DINE_IN_TYPE, ORDER_SECTIONS, ORDER_STATUS } from 'src/constants/order';
import client from 'src/services/client';
import logger from 'src/services/logger';
import { formatPrice } from 'src/utils/formatter';
import { getCancelToken } from 'src/utils/helpers';
import { history } from '~/services/history';
import { TOrder, orderCast } from '~/types/orders';
import getOrdersPath from '~/view/screens/CheckoutScreen/getOrdersPath';
import { toastSuccess } from '~/utils/toasts';
import { OrdersRatingStore } from '~/view/screens/OrdersRatingScreen/store/ordersRatingStore';

type TResponse = {
  message?: string;
};

export class OrderStore {
  constructor(public parentStore: LiveOrdersStore | CallCenterStore | OrdersHistoryStore | OrdersRatingStore) {
    makeObservable(this);
  }

  public get persist() {
    return false;
  }

  cancel: Canceler | null = null;
  @observable order: TOrder | null = null;
  @observable section: ORDER_SECTIONS = ORDER_SECTIONS.ORDER;
  @observable loading: boolean = false;
  @observable public lastOpenedOrder: number | string | null = null;
  @observable public lastOpenedOrderByCallCenter: number | string | null = null;

  @action public setOrder = (order: TOrder | TOrder['id'] | null, markAsRead: boolean | undefined = false) => {
    try {
      const orderId: number | null = (typeof order === 'object' ? Number(order?.id) : Number(order)) || null;
      // float numbers for id are used in unit tests
      if (!Number.isInteger(orderId)) return;

      if (!order) {
        this.order = null;
        return;
      }

      if (typeof order === 'string' || typeof order === 'number') {
        this.order = null;
        this.fetchOrder(order, markAsRead);
        return;
      }

      this.order = order;
      if (
        [ORDER_STATUS.PENDING, ORDER_STATUS.ACCEPTED, ORDER_STATUS.COMPLETED].includes(order.order_status_code) ||
        this.parentStore instanceof OrdersRatingStore
      ) {
        this.setSection(ORDER_SECTIONS.ORDER);
      } else if (order.order_status_code === ORDER_STATUS.CANCELED) {
        this.setSection(ORDER_SECTIONS.DETAILS);
      } else {
        if (getOrderDeliveryType(order.delivery_type_code) === DELIVERY_TYPES.DELIVERY) {
          this.setSection(ORDER_SECTIONS.DELIVERY);
        } else if (order.dinein?.dinein_delivery_type === ORDER_DINE_IN_TYPE.DINE_IN) {
          this.setSection(ORDER_SECTIONS.DINE_IN);
        } else if (getOrderDeliveryType(order.delivery_type_code) === DELIVERY_TYPES.PICKUP) {
          this.setSection(ORDER_SECTIONS.PICKUP);
        }
      }
      this.fetchOrder(order.id, markAsRead);
    } catch (err: any) {
      logger.log({ err, place: 'orderStore setOrder' });
      throw err;
    }
  };

  @action public setSection = (section: ORDER_SECTIONS) => {
    this.section = section;
  };

  @action public changeStatus = async (id: TOrder['id'], status: TOrder['order_status_code'], refund_message?: string) => {
    try {
      if (!this.order) return;
      this.loading = true;

      const response = await client.patch(`/v3/orders/${id}`, { status, refund_message });
      const order = orderCast(response);
      if (this.order?.id === order.id) this.order = order;

      if (this.parentStore instanceof CallCenterStore || this.parentStore instanceof LiveOrdersStore) {
        this.parentStore.handleUpdateOrder(order);
      }

      this.loading = false;

      if (status === ORDER_STATUS.CANCELED) {
        toastSuccess({
          title: localeStore.t('toast_callcenter_order_cancelled'),
          message: `#${String(response.order_key)} - ${localeStore.t(String(response.order_status_code) + '_status')}`,
        });
      }
      return;
    } catch (error: any) {
      this.loading = false;
      logger.error({ error, place: 'OrderStore changeStatus', showModal: true });
    }
  };

  @action updateResendSMSBtn = (timestamp: number) => {
    if (this.order?.sms) this.order.sms.resend_available_at = timestamp;
  };

  @action public exportPdf = async (id: TOrder['id']) => {
    try {
      const blob = await client.get(`/v3/orders/${id}/pdf`, { responseType: 'blob' });
      return blob;
    } catch (error: any) {
      logger.error({ error, place: 'OrderStore exportPdf', showModal: true });
    }
  };

  @action public requestDelivery = async (id: TOrder['id']): Promise<TResponse> => {
    try {
      const response = await client.post(`/v3/orders/${id}/request_delivery`);
      if (this.order?.id === id) {
        this.order.takergo = response.takergo;

        if (this.parentStore instanceof CallCenterStore || this.parentStore instanceof LiveOrdersStore) {
          this.parentStore.handleUpdateOrder(this.order);
        }
      }
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  @action public canсelDelivery = async (id: TOrder['id']): Promise<TResponse> => {
    try {
      const response = await client.post(`/v3/orders/${id}/cancel_delivery`);
      if (this.order?.id === id) {
        this.order.takergo = response.takergo;

        if (this.parentStore instanceof CallCenterStore || this.parentStore instanceof LiveOrdersStore) {
          this.parentStore.handleUpdateOrder(this.order);
        }
      }
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  @action private fetchOrder = async (id: TOrder['id'], markAsRead?: boolean) => {
    try {
      if (this.cancel) this.cancel('Cancelled');
      this.loading = true;
      const response = await client.get(`/v3/orders/${id}/details`, {
        params: { opened: markAsRead ? 1 : undefined },
        cancelToken: getCancelToken((c) => (this.cancel = c)),
      });

      const order = orderCast(response);

      this.lastOpenedOrder = id;

      this.loading = false;
      if (order) {
        this.order = order;
      }

      if (markAsRead && this.parentStore instanceof LiveOrdersStore) {
        this.parentStore.markOrderViewed(order.id);
      }

      return order;
    } catch (error: any) {
      if (error.message === 'Cancelled') return;

      this.loading = false;
      logger.error({
        error,
        place: 'OrderStore fetchOrder',
        showModal: true,
        modalProps: {
          id: 'order_view_error',
          onClose: () => {
            history.push(getOrdersPath(this.parentStore));
          },
        },
      });
    }
  };

  /**
   *
   * Format Price
   *
   * Take currency and currency position from {@link order}
   *
   * If {@link order} is undefined, calls just formatPrice
   *
   * @export
   * @param {string} price
   * @return {*}  {string}
   */
  public formatPrice(price: string | number): string {
    if (this.order) {
      return formatPrice(
        price,
        this.order?.currency?.currency_code || this.order.currency_code,
        this.order?.currency?.currency_position || this.order.currency_position,
        this.order?.currency?.divided
      );
    }
    return formatPrice(price);
  }

  @action clear = (cancelable: boolean) => {
    if (cancelable && this.cancel) this.cancel();
    this.order = null;
    this.lastOpenedOrder = null;
    this.lastOpenedOrderByCallCenter = null;
  };
}
